import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { EdsEntityUniqueFields } from '@/domains/milestones/types/EdsEntityUniqueFields';
import { MilestoneWithCategoryOptionsType } from '@/domains/milestones/types/EntityOptions';
import {
  AllMilestoneOptions,
  DeleteRequestedItemPayload,
  GetAvailableMilestonesPayload,
  GetMilestoneRelatedEntityListPayload,
  MilestoneValidationResult,
  NotValidItem,
  SetNextMilestonesPayload,
  UpdateRequestedItemPayload,
  ValidateCustomRequestedItemsPayload,
  ValidateMilestonePayload,
} from '@/domains/milestones/types/RequestedItemPayloads';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { AddRequestedItemFormFields } from '@/domains/milestones/components/AddRequestedItemModal/AddRequestedItemForm/addRequestedItemForm.config';
import { getKeyByUniqueFields } from '@/domains/milestones/utils/getKeyByUniqueFields';

type InitialState = {
  milestoneRelatedEntityList: NormalizedV2Response[];
  isLoading: boolean;
  addingInProgress: boolean;
  deletionInProgress: boolean;
  updatingInProgress: boolean;
  entityDetailsLoadingStateList: Record<string, boolean>;
  entityDetailsList: Record<string, NormalizedV2Response>;
  milestoneCategoryOptions: Partial<Record<EdsEntityCodes, Record<string, MilestoneWithCategoryOptionsType[]>>>;
  milestoneValidationResult: MilestoneValidationResult;
  notValidCustomRequestedItems: Record<string, NotValidItem[]>;
  isMilestoneOptionsLoading: boolean;
  allAvailableMilestones: AllMilestoneOptions;
  isAvaliableMilestonesLoading: boolean;
  isNotValidRequestedItemsLoading: boolean;
  isNotValidTasksLoading: boolean;
  notValidRequestedItemsData: NormalizedV2Response[];
  notValidTasksData: NormalizedV2Response[];
  isNextMilestonePossible: Record<string, boolean>;
};

const initialState: InitialState = {
  milestoneRelatedEntityList: [],
  isLoading: false,
  addingInProgress: false,
  deletionInProgress: false,
  updatingInProgress: false,
  entityDetailsLoadingStateList: {},
  entityDetailsList: {},
  milestoneCategoryOptions: {},
  milestoneValidationResult: {
    isValid: false,
    notValidRequestedItems: [],
    notValidTasks: [],
    resultedValidationStyle: null,
  },
  notValidCustomRequestedItems: {},
  isMilestoneOptionsLoading: false,
  allAvailableMilestones: {},
  isAvaliableMilestonesLoading: false,
  isNotValidRequestedItemsLoading: false,
  isNotValidTasksLoading: false,
  notValidRequestedItemsData: [],
  notValidTasksData: [],
  isNextMilestonePossible: {},
};

const milestonesSlice = createSlice({
  name: 'milestones',
  initialState,
  reducers: {
    addRequestedItem: (state, _action: PayloadAction<AddRequestedItemFormFields>) => {
      state.addingInProgress = true;
    },
    addRequestedItemSuccess: (state, _action: PayloadAction<void>) => {
      state.addingInProgress = false;
    },
    deleteRequestedItem: (state, _action: PayloadAction<DeleteRequestedItemPayload>) => {
      state.deletionInProgress = true;
    },
    deleteRequestedItemSuccess: (state, _action: PayloadAction<void>) => {
      state.deletionInProgress = false;
    },
    updateRequestedItem: (state, _action: PayloadAction<UpdateRequestedItemPayload>) => {
      state.updatingInProgress = true;
    },
    updateRequestedItemSuccess: (state, _action: PayloadAction<void>) => {
      state.updatingInProgress = false;
    },
    getMilestoneRelatedEntityList: (state, _action: PayloadAction<GetMilestoneRelatedEntityListPayload>) => {
      state.isLoading = true;
    },
    getMilestoneRelatedEntityListSuccess: (state, action: PayloadAction<NormalizedV2Response[]>) => {
      state.isLoading = false;
      state.milestoneRelatedEntityList = action.payload;
    },
    getEntityMilestoneDetails: (state, action: PayloadAction<EdsEntityUniqueFields>) => {
      state.entityDetailsLoadingStateList[`${getKeyByUniqueFields(action.payload)}`] = true;
    },
    getEntityMilestoneDetailsSuccess: (state, action: PayloadAction<NormalizedV2Response>) => {
      state.entityDetailsList[`${getKeyByUniqueFields(action.payload)}`] = action.payload;
      state.entityDetailsLoadingStateList[`${getKeyByUniqueFields(action.payload)}`] = false;
    },
    getEntityMilestoneDetailsError: (state, action: PayloadAction<EdsEntityUniqueFields>) => {
      state.entityDetailsLoadingStateList[`${getKeyByUniqueFields(action.payload)}`] = false;
    },
    getMilestoneWithCategoryOptions: (_state, _action: PayloadAction<GetAvailableMilestonesPayload>) => {},
    getMilestoneWithCategoryOptionsSuccess: (
      state,
      action: PayloadAction<{
        entityType: EdsEntityCodes;
        entityId: string;
        options: MilestoneWithCategoryOptionsType[];
      }>,
    ) => {
      const { entityType, entityId, options } = action.payload;
      const entityIdMap = state.milestoneCategoryOptions[entityType] || {};
      entityIdMap[entityId] = options;
      state.milestoneCategoryOptions[entityType] = entityIdMap;
    },
    setGetMilestoneOptionsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isMilestoneOptionsLoading = payload;
    },
    validateMilestone: (_state, _action: PayloadAction<ValidateMilestonePayload>) => {},
    validateMilestoneSuccess: (state, action: PayloadAction<MilestoneValidationResult>) => {
      state.milestoneValidationResult = action.payload;
    },
    resetMilestoneValidation: (state) => {
      state.notValidRequestedItemsData = initialState.notValidRequestedItemsData;
      state.notValidTasksData = initialState.notValidTasksData;
    },
    getNotValidRequestedItems: () => {},
    getNotValidRequestedItemsSuccess: (state, action: PayloadAction<NormalizedV2Response[]>) => {
      state.notValidRequestedItemsData = action.payload;
    },
    getNotValidTasks: () => {},
    getNotValidTasksSuccess: (state, action: PayloadAction<NormalizedV2Response[]>) => {
      state.notValidTasksData = action.payload;
    },
    getAllAvailableMilestones: () => {},
    getAllAvailableMilestonesSuccess: (state, action: PayloadAction<AllMilestoneOptions>) => {
      state.allAvailableMilestones = action.payload;
    },
    setNextMilestone: (_state, _action: PayloadAction<SetNextMilestonesPayload>) => {},
    setIsAvaliableMilestonesLoading(state, action: PayloadAction<boolean>) {
      state.isAvaliableMilestonesLoading = action.payload;
    },
    setIsNotValidRequestedItemsLoading(state, action: PayloadAction<boolean>) {
      state.isNotValidRequestedItemsLoading = action.payload;
    },
    setIsNotValidTasksLoading(state, action: PayloadAction<boolean>) {
      state.isNotValidTasksLoading = action.payload;
    },
    saveNewMilestoneState: (
      _state,
      _action: PayloadAction<Omit<ValidateMilestonePayload, 'onValid' | 'onInvalid'>>,
    ) => {},
    validateCustomRequestedItems: (_state, _action: PayloadAction<GetMilestoneRelatedEntityListPayload>) => {},
    validateCustomRequestedItemsSuccess: (state, { payload }: PayloadAction<ValidateCustomRequestedItemsPayload>) => {
      const entityKey = `${payload.entityType}-${payload.entityId}`;

      state.notValidCustomRequestedItems[entityKey] = payload.notValidCustomRequestedItems;
    },
    setIsNextMilestonePossible(state, action: PayloadAction<{ entityType: string; entityId: string; value: boolean }>) {
      const { entityType, entityId, value } = action.payload;
      state.isNextMilestonePossible[`${entityType}-${entityId}`] = value;
    },
  },
});

export const {
  getMilestoneRelatedEntityList,
  getMilestoneRelatedEntityListSuccess,
  getEntityMilestoneDetails,
  getEntityMilestoneDetailsSuccess,
  getEntityMilestoneDetailsError,
  addRequestedItem,
  addRequestedItemSuccess,
  deleteRequestedItem,
  deleteRequestedItemSuccess,
  updateRequestedItem,
  updateRequestedItemSuccess,
  getMilestoneWithCategoryOptions,
  getMilestoneWithCategoryOptionsSuccess,
  validateMilestone,
  validateMilestoneSuccess,
  resetMilestoneValidation,
  getAllAvailableMilestones,
  getAllAvailableMilestonesSuccess,
  setIsAvaliableMilestonesLoading,
  getNotValidRequestedItems,
  getNotValidRequestedItemsSuccess,
  getNotValidTasks,
  getNotValidTasksSuccess,
  saveNewMilestoneState,
  setGetMilestoneOptionsLoading,
  setIsNotValidRequestedItemsLoading,
  setIsNotValidTasksLoading,
  validateCustomRequestedItems,
  validateCustomRequestedItemsSuccess,
  setNextMilestone,
  setIsNextMilestonePossible,
} = milestonesSlice.actions;

export const milestonesReducer = milestonesSlice.reducer;
export const milestonesReducerPath = milestonesSlice.reducerPath;
